import {Component, computed, input} from '@angular/core';
import {CommentSegment, parseCommentSegments, splitWorkflowTag} from '../../../util/ta-comment.util';
import {TaWorkflowTagComponent} from './ta-workflow-tag';

/** Which bubble the text is sitting in - the two need different mention colours to read at all. */
export type TaCommentTone = 'own' | 'other';

export const CHIP_BASE = 'rounded px-1 py-px font-semibold cursor-default';

/**
 * Small inner component used to properly render TA comments with mentions.
 *
 * One comment's text, with its @mentions drawn as mentions and its workflow tag, if it has one,
 * as a pill at the head of the first line - inline, like the mentions, so a one-line note stays
 * one line.
 *
 * Takes the stored text and nothing else. Each mention carries its own display name, so there is
 * no list to be passed in. Rendered as segments of spans.
 */
@Component({
  selector: 'ta-comment-text',
  imports: [TaWorkflowTagComponent],
  template: `
    @if (tagKey(); as key) {
      <ta-workflow-tag class="mr-2" [key]="key"/>
    }
    @for (segment of segments(); track $index) {
      @if (segment.kind === 'mention') {
        <span [class]="mentionClass()">{{ '@' + segment.name }}</span>
      } @else if (segment.kind === 'text') {
        <span>{{ segment.text }}</span>
      }
    }
  `,
  host: {class: 'block whitespace-pre-wrap break-words'},
})
export class TaCommentTextComponent {
  /** The stored comment text, mentions and all. */
  text = input<string | null | undefined>('');
  tone = input<TaCommentTone>('other');

  private split = computed(() => splitWorkflowTag(this.text()));
  protected tagKey = computed<string | undefined>(() => this.split().key);
  protected segments = computed<CommentSegment[]>(() => parseCommentSegments(this.split().body));

  protected mentionClass = computed<string>(() =>
    this.tone() === 'own'
      ? `${CHIP_BASE} bg-white/20 text-yellow-200`
      : `${CHIP_BASE} bg-teal-100 text-teal-600`,
  );
}
